import CloseIcon from "@mui/icons-material/CancelRounded";
import { Button, Popover, Typography } from "@mui/material";
import type { MouseEventHandler } from "react";
import { useState } from "react";
import { useFilterContext } from "./FilterContext";
import FilterTypeIcon from "./FilterTypeIcon";
import FilterEditor from "./editors/FilterEditor";
import { removeFilterAction } from "./filterState";
import type { TableFilter } from "./filterTypes";
import { formatFilterValue, isFilterActive } from "./handlers/filterHandlers";

interface Props {
  filter: TableFilter;
  disabled?: boolean;
}

const FilterButton = ({ filter, disabled }: Props) => {
  const { setFilterState } = useFilterContext();

  const [editing, setEditing] = useState(false);
  const [buttonEl, setButtonEl] = useState<HTMLButtonElement | null>(null);

  const handleRemoveClick: MouseEventHandler = (e) => {
    e.stopPropagation();
    setFilterState(removeFilterAction(filter.id));
  };

  const activeFilter = isFilterActive(filter);

  const isDisabled = disabled || filter.readOnly;

  return (
    <>
      <Button
        variant="outlined"
        color={activeFilter ? "primary" : "secondary"}
        ref={(el) => setButtonEl(el)}
        disabled={isDisabled}
        onClick={() => setEditing(!editing)}
        startIcon={<FilterTypeIcon filterType={filter.type} />}
        sx={{
          maxWidth: "21rem",
          "&.Mui-disabled": {
            "& .MuiButton-startIcon": { color: "secondary.light" },
            "& .MuiTypography-root": { color: "text.secondary" },
          },
        }}
      >
        <Typography noWrap>{formatFilterValue(filter)}</Typography>

        {!isDisabled && (
          <CloseIcon
            onClick={handleRemoveClick}
            sx={{
              ml: 0.5,
              width: 16,
              height: 16,
              color: activeFilter ? "primary.light" : "secondary.light",
              "&:hover": { color: activeFilter ? "primary.main" : "secondary.main" },
            }}
          />
        )}
      </Button>

      <Popover
        open={editing}
        onClose={() => setEditing(false)}
        anchorEl={buttonEl}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <FilterEditor filter={filter} />
      </Popover>
    </>
  );
};

export default FilterButton;
